import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { HttpErrorResponse } from '@angular/common/http';
import { AppModelModalComponent } from '@nimmsta/ng-commons-lib/forms';
import { AppDialogService } from '@nimmsta/ng-commons-lib/services/app-dialog';
import { AuthService } from '@nimmsta/ng-commons-lib/auth';
import {
  Column,
  CustomAction,
  DataTableComponent,
  NimmstaDataSource,
  EmptyTableComponent,
} from '@nimmsta/ng-commons-lib/data-table';
import {
  StatusBadgePipe,
  UserRolesPipe,
  DateTimePipe,
} from '@nimmsta/ng-commons-lib/pipes';
import { User, UserStatus } from '@nimmsta/ng-commons-lib/models';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { PartnerOrganizationService } from '@nimmsta/ng-commons-lib/services/partner-organization';
import {
  CheckSecondaryUserAction,
  DeleteUserAction,
  LockUnlockAction,
} from '@nimmsta/ng-b2b-lib/user';
import { OrganizationUserService } from '@nimmsta/ng-b2b-lib/services/organization-user';
import { ActivatedRoute } from '@angular/router';
import { PartnerUserFormComponent } from './partner-user-form.component';
import { AddSecondaryUserToOrgModalComponent } from './add-secondary-user-to-org-modal.component';

_('user.title');
@Component({
  selector: 'app-partner-user-overview',
  templateUrl: './partner-user-overview.component.html',
  imports: [
    TranslatePipe,
    DataTableComponent,
    EmptyTableComponent,
    AppModelModalComponent,
    ErrorComponent,
    HeaderBarComponent,
    PartnerUserFormComponent,
  ],
  providers: [StatusBadgePipe, DateTimePipe, UserRolesPipe],
})
export class PartnerUserOverviewComponent implements OnInit {
  private orgService = inject(PartnerOrganizationService);
  private userService = inject(OrganizationUserService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private statusBadge = inject(StatusBadgePipe);
  private dateTimePipe = inject(DateTimePipe);
  private userRolesPipe = inject(UserRolesPipe);
  private route = inject(ActivatedRoute);
  private modalService = inject(AppDialogService);

  title?: string;

  userSource!: NimmstaDataSource<User>;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<User>;

  @ViewChild('EditModal', { static: false })
  private editModalComponent!: AppModelModalComponent<User>;

  @ViewChild(PartnerUserFormComponent, { static: true })
  private userFormComponent!: PartnerUserFormComponent;

  customActions: CustomAction<User>[] = [];

  customTableActions: CustomAction<User>[] = [];

  private orgId?: string;

  error?: HttpErrorResponse;

  get columns(): Column[] {
    return [
      new Column('family_name', 'user.family_name', { sortable: true }),
      new Column('given_name', 'user.given_name', { sortable: true }),
      new Column('email', 'user.email', { sortable: true }),
      new Column('roles', 'user.roles', { pipe: this.userRolesPipe }),
      new Column('status', 'user.status', {
        sortable: true,
        pipe: this.statusBadge,
      }),
      new Column('createdAt', 'user.createdAt', {
        pipe: this.dateTimePipe,
        sortable: true,
      }),
    ];
  }

  ngOnInit(): void {
    const orgId = this.route.snapshot.paramMap.get('id');
    if (!orgId) {
      throw new Error('Organization id is required');
    }
    this.orgId = orgId;
    this.orgService.get(this.orgId).subscribe((org) => {
      this.title = org.name;
    });
    this.userService.setOrgId(this.orgId);
    this.userSource = new NimmstaDataSource<User>(this.userService);
    const addSecondaryUserAction = new CustomAction('user.secondary-user.add');
    addSecondaryUserAction.subscribe(() => this.showAddSecondaryUserModal());
    this.customTableActions.push(addSecondaryUserAction);
    const editAction = new CheckSecondaryUserAction('edit', false);
    editAction.subscribe((id) => this.edit(id));
    this.customActions.push(editAction);
    const deleteAction = new DeleteUserAction('delete', this.authService);
    deleteAction.subscribe((id) => this.delete(id));
    this.customActions.push(deleteAction);
    const removeAction = new CheckSecondaryUserAction(
      'user.secondary-user.remove',
      true,
    );
    removeAction.subscribe((id) => this.removeSecondaryOrg(id));
    this.customActions.push(removeAction);
    const lockUnlockAction = new LockUnlockAction('user.unlock');
    this.customActions.push(lockUnlockAction);
    lockUnlockAction.subscribe((id) => this.toggleLock(id));
  }

  new(): void {
    this.translate.get('user.new').subscribe((value) => {
      const user = {} as User;
      user.organizationId = this.orgId;
      this.openModal(value, true, user);
    });
  }

  edit(id: string): void {
    this.translate.get('user.edit').subscribe((value) => {
      this.openModal(value, true, this.getSelectedObject(id)!);
    });
  }

  openModal(title: string, editable: boolean, user: User): void {
    this.userFormComponent.editable = editable;
    this.userFormComponent.setValue(user);
    this.editModalComponent.title = title;
    this.editModalComponent.editable = editable;
    this.editModalComponent.show('sm');
  }

  delete(id: string): void {
    this.userService.delete(this.getSelectedObject(id)!).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error) => {
        this.error = error;
      },
    });
  }

  showAddSecondaryUserModal(): void {
    const modalRef = this.modalService.open<
      AddSecondaryUserToOrgModalComponent,
      { secondaryOrganizationId: string },
      boolean
    >(AddSecondaryUserToOrgModalComponent, {
      data: { secondaryOrganizationId: this.orgId! },
      size: 'sm',
    });

    modalRef.closed.subscribe((created) => {
      if (created === true) {
        this.tableComponent.loadDataPage();
      }
    });
  }

  removeSecondaryOrg(id: string): void {
    this.userService.removeSecondaryOrg(id).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error) => {
        this.error = error;
      },
    });
  }

  toggleLock(id: string): void {
    const user = this.getSelectedObject(id)!;
    if (user.status === UserStatus.ACTIVE) {
      this.lock(id);
    } else {
      this.unlock(id);
    }
  }

  lock(id: string): void {
    this.userService.lock(this.getSelectedObject(id)!).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error) => {
        this.error = error;
      },
    });
  }

  unlock(id: string): void {
    this.userService.unlock(this.getSelectedObject(id)!).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error) => {
        this.error = error;
      },
    });
  }

  onSave(): void {
    this.userFormComponent.onSave();
  }

  onModalClose(): void {
    this.editModalComponent.hide();
    this.tableComponent.loadDataPage();
  }

  getSelectedObject(id: string): User | undefined {
    return this.userSource.data?.content.filter((d) => id === d.id).pop();
  }

  onLoading(loading: boolean): void {
    this.editModalComponent.loading = loading;
  }
}
